import React, { useEffect, useRef, useState } from 'react';

const MapaVisitasObras = ({ visitas = [], obras = [] }) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersLayerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Parsear LatLong string a coordenadas
  const parseLatLong = (latLongString) => {
    if (!latLongString || typeof latLongString !== 'string') return null;
    const parts = latLongString.split(',').map(p => parseFloat(p.trim()));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      return { lat: parts[0], lng: parts[1] };
    }
    return null;
  };

  useEffect(() => {
    // Solo inicializar si Leaflet está disponible y el mapa no existe
    if (!window.L || mapInstanceRef.current) return;

    if (!mapContainerRef.current) return;

    // Inicializar mapa
    const map = window.L.map(mapContainerRef.current, {
      attributionControl: false
    }).setView([40.4168, -3.7038], 6); // España

    // Agregar tile layer
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);

    // Crear capa para marcadores
    markersLayerRef.current = window.L.layerGroup().addTo(map);

    mapInstanceRef.current = map;

    // Cleanup al desmontar
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      if (markersLayerRef.current) {
        markersLayerRef.current = null;
      }
    };
  }, []);

  // Efecto para actualizar marcadores cuando cambien visitas u obras
  useEffect(() => {
    if (!mapInstanceRef.current || !markersLayerRef.current) return;

    // Limpiar marcadores existentes
    markersLayerRef.current.clearLayers();

    // Agregar marcadores de visitas (azul)
    visitas.forEach((visita) => {
      const coords = parseLatLong(visita.LatLong);
      if (!coords) return;

      // Crear marcador circular azul para visitas (sin transparencia)
      const marker = window.L.circleMarker([coords.lat, coords.lng], {
        radius: 8,
        fillColor: '#2563eb', // Azul sólido
        color: '#ffffff',
        weight: 2,
        opacity: 1,
        fillOpacity: 1 // Sin transparencia
      });

      // Contenido del popup para visitas
      const popupContent = `
        <div style="padding: 16px; font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;">
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 2px solid #2563eb;">
            <div style="background: #dbeafe; padding: 10px; border-radius: 10px; flex-shrink: 0;">
              <svg style="width: 22px; height: 22px; color: #2563eb;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 style="margin: 0; font-size: 16px; font-weight: 700; color: #1e40af; word-break: break-word;">Visita ${visita.Oferta || 'N/A'}</h3>
          </div>
          
          <div style="display: grid; grid-template-columns: auto 1fr; gap: 10px 16px; align-items: start;">
            ${visita.Cliente ? `
              <span style="font-weight: 600; color: #4b5563; font-size: 13px;">Cliente:</span>
              <span style="color: #111827; font-size: 13px; word-break: break-word;">${visita.Cliente}</span>
            ` : ''}
            
            ${visita.Comercial ? `
              <span style="font-weight: 600; color: #4b5563; font-size: 13px;">Comercial:</span>
              <span style="color: #111827; font-size: 13px; word-break: break-word;">${visita.Comercial}</span>
            ` : ''}
            
            ${visita.FechaVisita ? `
              <span style="font-weight: 600; color: #4b5563; font-size: 13px;">Fecha:</span>
              <span style="color: #111827; font-size: 13px;">${new Date(visita.FechaVisita).toLocaleDateString('es-ES')}</span>
            ` : ''}
            
            ${visita.TipoVisita ? `
              <span style="font-weight: 600; color: #4b5563; font-size: 13px;">Tipo:</span>
              <span style="color: #111827; font-size: 13px; word-break: break-word;">${visita.TipoVisita}</span>
            ` : ''}
            
            ${visita.EstadoOferta ? `
              <span style="font-weight: 600; color: #4b5563; font-size: 13px;">Estado:</span>
              <span style="background: #dbeafe; color: #1e40af; padding: 4px 10px; border-radius: 6px; font-size: 12px; font-weight: 600; display: inline-block; width: fit-content;">${visita.EstadoOferta}</span>
            ` : ''}
            
            ${visita.Direccion ? `
              <span style="font-weight: 600; color: #4b5563; font-size: 13px; grid-column: 1; padding-top: 8px; border-top: 1px solid #e5e7eb;">Ubicación:</span>
              <span style="color: #6b7280; font-size: 12px; word-break: break-word; grid-column: 2; padding-top: 8px; border-top: 1px solid #e5e7eb;">${visita.Direccion}</span>
            ` : ''}
          </div>
        </div>
      `;

      marker.bindPopup(popupContent, {
        maxWidth: window.innerWidth < 640 ? window.innerWidth - 40 : 400,
        minWidth: window.innerWidth < 640 ? 280 : 350,
        className: 'custom-popup'
      });

      marker.addTo(markersLayerRef.current);
    });

    // Agregar marcadores de obras (púrpura)
    obras.forEach((obra) => {
      const coords = parseLatLong(obra.LatLong);
      if (!coords) return;

      // Crear marcador circular púrpura para obras (sin transparencia)
      const marker = window.L.circleMarker([coords.lat, coords.lng], {
        radius: 8,
        fillColor: '#9333ea', // Púrpura sólido
        color: '#ffffff',
        weight: 2,
        opacity: 1,
        fillOpacity: 1, // Sin transparencia
      });
      

      // Contenido del popup para obras
      const popupContent = `
        <div style="padding: 16px; font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;">
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 2px solid #9333ea;">
            <div style="background: #f3e8ff; padding: 10px; border-radius: 10px; flex-shrink: 0;">
              <svg style="width: 22px; height: 22px; color: #9333ea;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 style="margin: 0; font-size: 16px; font-weight: 700; color: #7c3aed; word-break: break-word;">Obra ${obra.Obra || 'N/A'}</h3>
          </div>
          
          <div style="display: grid; grid-template-columns: auto 1fr; gap: 10px 16px; align-items: start;">
            ${obra.Constructora ? `
              <span style="font-weight: 600; color: #4b5563; font-size: 13px;">Constructora:</span>
              <span style="color: #111827; font-size: 13px; word-break: break-word;">${obra.Constructora}</span>
            ` : ''}
            
            ${obra.Comercial ? `
              <span style="font-weight: 600; color: #4b5563; font-size: 13px;">Comercial:</span>
              <span style="color: #111827; font-size: 13px; word-break: break-word;">${obra.Comercial}</span>
            ` : ''}
            
            ${obra.EstadoObra ? `
              <span style="font-weight: 600; color: #4b5563; font-size: 13px;">Estado:</span>
              <span style="background: #f3e8ff; color: #7c3aed; padding: 4px 10px; border-radius: 6px; font-size: 12px; font-weight: 600; display: inline-block; width: fit-content;">${obra.EstadoObra}</span>
            ` : ''}
            
            ${obra.Subdivision ? `
              <span style="font-weight: 600; color: #4b5563; font-size: 13px;">Zona:</span>
              <span style="color: #111827; font-size: 13px; word-break: break-word;">${obra.Subdivision}</span>
            ` : ''}
            
            ${obra.Direccion ? `
              <span style="font-weight: 600; color: #4b5563; font-size: 13px; grid-column: 1; padding-top: 8px; border-top: 1px solid #e5e7eb;">Ubicación:</span>
              <span style="color: #6b7280; font-size: 12px; word-break: break-word; grid-column: 2; padding-top: 8px; border-top: 1px solid #e5e7eb;">${obra.Direccion}</span>
            ` : ''}
          </div>
        </div>
      `;

      marker.bindPopup(popupContent, {
        maxWidth: window.innerWidth < 640 ? window.innerWidth - 40 : 400,
        minWidth: window.innerWidth < 640 ? 280 : 350,
        className: 'custom-popup'
      });

      marker.addTo(markersLayerRef.current);
    });

    // Ajustar vista si hay marcadores
    const allCoords = [
      ...visitas.map(v => parseLatLong(v.LatLong)).filter(Boolean),
      ...obras.map(o => parseLatLong(o.LatLong)).filter(Boolean)
    ];

    if (allCoords.length > 0) {
      const bounds = window.L.latLngBounds(allCoords.map(c => [c.lat, c.lng]));
      mapInstanceRef.current.fitBounds(bounds, { 
        padding: [50, 50],
        maxZoom: 12
      });
    }

  }, [visitas, obras]);

  // Efecto para redimensionar el mapa cuando cambia fullscreen
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    // Invalidar tamaño después de cambiar
    setTimeout(() => {
      mapInstanceRef.current.invalidateSize();
    }, 100);
  }, [isFullscreen]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div 
      className={`transition-all duration-300 ${
        isFullscreen 
          ? 'fixed inset-0 z-50 bg-gray-900/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 lg:p-8' 
          : 'relative rounded-xl shadow-sm'
      }`}
    >
      {/* Botón de pantalla completa/minimizar */}
      <button
        onClick={toggleFullscreen}
        className={`absolute bg-white hover:bg-gray-50 text-gray-700 transition-all duration-200 hover:scale-105 border border-gray-200 ${
          isFullscreen ? 'top-10 right-10' : 'top-4 right-4'
        }`}
        style={{ 
          zIndex: 1001,
          width: '36px',
          height: '36px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
        }}
        title={isFullscreen ? 'Minimizar mapa' : 'Ver en pantalla completa'}
      >
        {isFullscreen ? (
          // Icono minimizar (X)
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          // Icono expandir
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
          </svg>
        )}
      </button>

      {/* Leyenda del mapa */}
      <div 
        className={`absolute bg-white rounded-lg shadow-lg border border-gray-200 p-3 ${
          isFullscreen ? 'bottom-10 left-10' : 'bottom-4 left-4'
        }`}
        style={{ zIndex: 1000 }}
      >
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <div 
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: '#2563eb',
                border: '2px solid #ffffff',
                boxShadow: '0 0 0 1px #2563eb'
              }}
            ></div>
            <span className="text-xs text-gray-600">Visitas {visitas.filter(v => parseLatLong(v.LatLong)).length}</span>
          </div>
          <div className="flex items-center gap-2">
            <div 
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: '#9333ea',
                border: '2px solid #ffffff',
                boxShadow: '0 0 0 1px #9333ea'
              }}
            ></div>
            <span className="text-xs text-gray-600">Obras {obras.filter(o => parseLatLong(o.LatLong)).length}</span>
          </div>
        </div>
      </div>

      {/* Contenedor del mapa */}
      <div 
        ref={mapContainerRef} 
        className="w-full rounded-xl overflow-hidden"
        style={{ 
          height: isFullscreen ? 'calc(100vh - 4rem)' : '500px',
          maxWidth: isFullscreen ? '100%' : 'none',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
        }}
      ></div>
      
      <style jsx="true">{`
        /* Mejorar controles de zoom */
        .leaflet-control-zoom {
          border: none !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
          border-radius: 12px !important;
          overflow: hidden;
        }
        
        .leaflet-control-zoom a {
          width: 36px !important;
          height: 36px !important;
          line-height: 36px !important;
          font-size: 20px !important;
          font-weight: 600 !important;
          color: #374151 !important;
          background: #ffffff !important;
          border: none !important;
          transition: all 0.2s ease !important;
        }
        
        .leaflet-control-zoom a:hover {
          background: #f3f4f6 !important;
          color: #111827 !important;
          transform: scale(1.05);
        }
        
        .leaflet-control-zoom a:first-child {
          border-bottom: 1px solid #e5e7eb !important;
        }
        
        .leaflet-control-zoom-in {
          border-radius: 12px 12px 0 0 !important;
        }
        
        .leaflet-control-zoom-out {
          border-radius: 0 0 12px 12px !important;
        }

        /* Mejorar estilo del popup */
        .leaflet-popup-content-wrapper {
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1);
          padding: 0;
          overflow: hidden;
        }
        
        .leaflet-popup-content {
          margin: 0;
        }
        
        .leaflet-popup-tip {
          box-shadow: 0 3px 14px rgba(0, 0, 0, 0.15);
        }

        /* Mejorar botón de cerrar (X) del popup */
        .leaflet-popup-close-button {
          width: 28px !important;
          height: 28px !important;
          padding: 6px !important;
          font-size: 18px !important;
          font-weight: 700 !important;
          line-height: 1 !important;
        }
        
        .leaflet-popup-close-button:hover {
          color: red !important;
          opacity: 1 !important;
        }
      `}</style>
    </div>
  );
};

export default MapaVisitasObras;
